/* eslint-disable no-underscore-dangle */
/**
 * ONE settlement currency's statement: its four figures, the trips behind
 * them, and the note that says what the amounts are in.
 *
 * This is the whole of the Payments screen for the ~120 operators who settle
 * in one currency — it renders exactly what that screen has always rendered.
 * A company that settles in two holds two operator records, one per
 * currency, and gets one of these per record, one after the other. They are
 * never added up: no rate makes a EUR figure and a HUF figure into a
 * meaningful total, and the two records exist so that nobody has to try.
 */
import React from 'react';
import { formatTime } from '../../../Utils/timezone';
import StatementSummary from './StatementSummary';
import {
  formatMoney, plural, routeLabel, shortDay,
} from './operatorFormat';

export default function StatementBucket({ bucket, title, periodLabel }) {
  const { currency } = bucket;
  const lines = bucket.lines || [];

  return (
    <>
      <StatementSummary
        statement={bucket}
        title={title}
        subtitle={`${plural(Number(bucket.trip_count) || 0, 'trip', 'trips')} · settled in ${currency}`}
      />

      <div className="pp-op-card">
        {!lines.length ? <div className="pp-op-empty">No trips in this month.</div> : (
          <table className="pp-op-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Reference</th>
                <th scope="col">Route</th>
                <th scope="col">Status</th>
                <th scope="col" className="is-num">Your fee</th>
                <th scope="col" className="is-num">Cash collected</th>
                <th scope="col" className="is-num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line._id || line.booking_reference}>
                  <td data-label="Date">
                    {`${shortDay(line.date_datetime)} ${formatTime(line.date_datetime)}`.trim()}
                  </td>
                  <td data-label="Reference"><span className="pp-op-ref">{line.booking_reference}</span></td>
                  <td data-label="Route">
                    <span className="pp-op-route">{routeLabel(line)}</span>
                    {line.customer_name ? <span className="pp-op-small">{line.customer_name}</span> : null}
                  </td>
                  <td data-label="Status">{line.status}</td>
                  <td data-label="Your fee" className="is-num">{formatMoney(line.fee, currency)}</td>
                  <td data-label="Cash collected" className="is-num">
                    {line.cash_collected ? `− ${formatMoney(line.cash_collected, currency)}` : '—'}
                  </td>
                  <td data-label="Balance" className="is-num">{formatMoney(line.balance, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="pp-op-footnote">
        {`Amounts are in ${currency}. Anything agreed in another currency is converted at the rate for the end of ${periodLabel}. `
          + '"Already paid" counts the payments Pickup Hungary has made for these trips.'}
      </p>
    </>
  );
}
